import { defineComponent as _defineComponent } from 'vue'
import { unref as _unref, createVNode as _createVNode, createElementVNode as _createElementVNode, toDisplayString as _toDisplayString, createTextVNode as _createTextVNode, withCtx as _withCtx, openBlock as _openBlock, createBlock as _createBlock, createCommentVNode as _createCommentVNode, resolveComponent as _resolveComponent, createElementBlock as _createElementBlock } from "vue"

const _hoisted_1 = {
  class: "d-flex align-items-center justify-content-center",
  style: {"min-height":"100px"}
}
const _hoisted_2 = { key: 0 }
const _hoisted_3 = { key: 1 }
const _hoisted_4 = { key: 0 }
const _hoisted_5 = { key: 1 }
const _hoisted_6 = { key: 2 }
const _hoisted_7 = { key: 0 }
const _hoisted_8 = { key: 1 }
const _hoisted_9 = { key: 2 }
const _hoisted_10 = { key: 0 }
const _hoisted_11 = { key: 1 }
const _hoisted_12 = { key: 2 }
const _hoisted_13 = { key: 0 }
const _hoisted_14 = { key: 1 }
const _hoisted_15 = { key: 2 }

import { computed, ref } from 'vue';
import {
  BAlert,
  BBadge,
  BButton,
  BButtonGroup,
  BCard,
  BFormCheckbox,
  BFormSelect,
  BModal,
  BSpinner,
  BTable,
  BFormTextarea,
} from 'bootstrap-vue-next';
import { useMutation } from '@vue/apollo-composable';
import {
  OrganizationOwnerUpdateDocument,
  OrganizationUserAssociationUpdateDocument,
  OrganizationUserAssociationRemoveDocument,
  OrganizationUserAssociationType,
} from '@/gql/graphql';
import { formatDate } from '@/utils/date-utils';
import { useUserStore } from '@/store/user';
import {
  ORGANIZATION_USER_ROLES,
  OrganizationMembershipModeSelectOptions,
} from '@/constants';
import OrganizationActivate from '@/components/organization/OrganizationActivate.vue';

// --- Props for the component ---
interface Props {
  associations: OrganizationUserAssociationType[];
  isLoading: boolean;
  isError: boolean;
  canManage?: boolean;
  isOwner?: boolean;
  showActiveStatus?: boolean;
  showEmail?: boolean;
  showOrganization?: boolean;
}

export default /*@__PURE__*/_defineComponent({
  __name: 'OrganizationMembershipList',
  props: {
    associations: {},
    isLoading: { type: Boolean },
    isError: { type: Boolean },
    canManage: { type: Boolean },
    isOwner: { type: Boolean },
    showActiveStatus: { type: Boolean },
    showEmail: { type: Boolean },
    showOrganization: { type: Boolean }
  },
  emits: ['membershipsUpdated', 'rowClicked'],
  setup(__props: any, { emit: __emit }) {

const props = __props;
const emits = __emit;

const userStore = useUserStore();

// Refs to handle the modal & the ID of the association to remove
const showAssocActivateModal = ref(false);
const showAssocEditModal = ref(false);
const showUpdateOwnerModal = ref(false);
const showRemoveModal = ref(false);
const assocToActivate = ref<OrganizationUserAssociationType | null>(null);
const assocToEdit = ref<OrganizationUserAssociationType | null>(null);
const assocNewOwner = ref<OrganizationUserAssociationType | null>(null);
const assocToRemove = ref<OrganizationUserAssociationType | null>(null);

// --- Highlight active association ---
const isAssocActive = (
  assoc: OrganizationUserAssociationType | null
): boolean => {
  if (!assoc) return false;
  if (assoc.removedAt) return false;
  return (
    assoc.organization?.id === userStore.activeOrgAssociation?.organization?.id
  );
};

// --- Show inactive associations ---
const isAssocInactive = (
  assoc: OrganizationUserAssociationType | null
): boolean => {
  if (!assoc) return true;
  return !!assoc.removedAt;
};

const showInactiveAssociations = ref(false);

// --- Computed properties ---
// compute property to check whether user is owner and the only remaining active member
const isOwnerAndLastActiveMember = computed(() => {
  if (!props.associations) return false;
  const activeMembers = props.associations.filter(
    (assoc) => !isAssocInactive(assoc)
  );
  return (
    activeMembers.length === 1 &&
    activeMembers[0].user?.id === userStore.id &&
    props.isOwner
  );
});

const filteredAssociations = computed(() => {
  if (!props.associations) return [];

  let associations = props.associations;

  if (!showInactiveAssociations.value) {
    associations = associations.filter((key) => !isAssocInactive(key));
  }

  return associations.map((key) => ({
    ...key, // Spread original key properties
    _rowVariant: getRowVariant(key), // Assign row variant dynamically
  }));
});

const getRowVariant = (
  item: OrganizationUserAssociationType | null
): string => {
  if (!item) return '';
  // if (isAssocActive(item)) { return 'primary'; }
  if (isAssocInactive(item)) {
    return 'danger';
  }
  return ''; // previously 'success';
};

// --- Association table fields (dynamic)  ---
const assocTableFields = computed(() => {
  const fields = [
    { key: 'userRole', label: 'Role', sortable: true, tdClass: 'align-middle' },
    {
      key: 'description',
      label: 'Description',
      sortable: true,
      tdClass: 'align-middle',
    },
    {
      key: 'createdAt',
      label: 'From',
      sortable: true,
      formatter: formatDate,
      tdClass: 'align-middle',
    },
    {
      key: 'removedAt',
      label: 'To',
      sortable: true,
      formatter: (v: Date | string | undefined | null) => formatDate(v, '-'),
      tdClass: 'align-middle',
    },
    { key: 'id', label: '', tdClass: 'align-middle text-end min-width' },
  ];

  if (props.showEmail) {
    fields.splice(0, 0, {
      key: 'user.email',
      label: 'E-mail',
      sortable: true,
      tdClass: 'align-middle',
    });
    fields.splice(0, 0, {
      key: 'userName',
      label: 'Name',
      sortable: true,
      tdClass: 'align-middle',
    });
  }

  if (props.showOrganization) {
    fields.splice(0, 0, {
      key: 'organizationName',
      label: 'Organization',
      sortable: true,
      tdClass: 'align-middle',
    });
  }

  return fields;
});

// --- Initialize Organization Mutations ---
const showOrgOwnerUpdateSuccess = ref(false);
const showAssocUpdateSuccess = ref(false);
const showAssocRemoveSuccess = ref(false);

const loadingOrgOwnerUpdate = ref(false);
const loadingAssocUpdate = ref(false);
const loadingAssocRemove = ref(false);

const errorOrgOwnerUpdate = ref<string | null>(null);
const errorAssocUpdate = ref<string | null>(null);
const errorAssocRemove = ref<string | null>(null);

const {
  mutate: updateOrgOwnerMut,
  onDone: onUpdateOrgOwnerDone,
  onError: onUpdateOrgOwnerError,
} = useMutation(OrganizationOwnerUpdateDocument);
const {
  mutate: updateOrgAssociationMut,
  onDone: onUpdateOrgAssociationDone,
  onError: onUpdateOrgAssociationError,
} = useMutation(OrganizationUserAssociationUpdateDocument);
const {
  mutate: removeOrgAssociationMut,
  onDone: onRemoveOrgAssociationDone,
  onError: onRemoveOrgAssociationError,
} = useMutation(OrganizationUserAssociationRemoveDocument);

// --- Handle mutation responses ---
onUpdateOrgOwnerDone((res) => {
  loadingOrgOwnerUpdate.value = false;
  if (res.data && res.data.organizationOwnerUpdate) {
    console.debug(
      'Organization owner updated:',
      res.data.organizationOwnerUpdate
    );
    // emit event so the parent can refresh the memberships
    showOrgOwnerUpdateSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocNewOwner.value = null;
      showUpdateOwnerModal.value = false;
      showOrgOwnerUpdateSuccess.value = true;
    }, 2000);
  } else {
    console.error('Organization owner update failed:', res.data);
    errorAssocRemove.value = `Organization owner update failed: ${res.data}`;
  }
});
onUpdateOrgOwnerError((err) => {
  console.error('Error updating organization owner:', err);
  loadingOrgOwnerUpdate.value = false;
  errorOrgOwnerUpdate.value = err.message;
});
onUpdateOrgAssociationDone((res) => {
  loadingAssocUpdate.value = false;
  if (res.data && res.data.organizationUserAssociationUpdate) {
    console.debug(
      'Organization-user association updated:',
      res.data.organizationUserAssociationUpdate
    );
    // emit event so the parent can refresh the memberships
    showAssocUpdateSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocToEdit.value = null;
      showAssocEditModal.value = false;
      showAssocUpdateSuccess.value = false;
    }, 2000);
  } else {
    console.error('Organization-user association update failed:', res.data);
    errorAssocUpdate.value = `Organization-user association update failed: ${res.data}`;
  }
});
onUpdateOrgAssociationError((err) => {
  console.error('Error updating organization-user association:', err);
  loadingAssocUpdate.value = false;
  errorAssocUpdate.value = err.message;
});
onRemoveOrgAssociationDone((res) => {
  loadingAssocRemove.value = false;
  if (res.data && res.data.organizationUserAssociationRemove) {
    console.debug(
      'Organization-user association removed:',
      res.data.organizationUserAssociationRemove
    );
    // emit event so the parent can refresh the memberships
    showAssocRemoveSuccess.value = true;
    emits('membershipsUpdated');
    // close the modal with delay
    setTimeout(() => {
      assocToRemove.value = null;
      showRemoveModal.value = false;
      showAssocRemoveSuccess.value = false;
    }, 2000);
  } else {
    console.error('Organization-user association removal failed:', res.data);
    errorAssocRemove.value = `Organization-user association removal failed: ${res.data}`;
  }
});
onRemoveOrgAssociationError((err) => {
  console.error('Error removing organization-user association:', err);
  loadingAssocRemove.value = false;
  errorAssocRemove.value = err.message;
});

// -- Handle mutation requests ---
// --- handle MemberShipList emits ---
function handleUpdateAssociationRequest() {
  if (!assocToEdit.value) {
    console.error('No association to update');
    return;
  }

  if (!assocToEdit.value.id) {
    console.error('No association ID specified in the update request');
    return;
  }
  console.debug(
    'Update of organization-user association',
    assocToEdit.value.id,
    'requested'
  );
  errorAssocUpdate.value = null;
  loadingAssocUpdate.value = true;
  updateOrgAssociationMut({
    id: assocToEdit.value.id,
    userRole: assocToEdit.value.userRole,
    description: assocToEdit.value.description,
  });
}
function handleUpdateOrgOwnerRequest() {
  if (!assocNewOwner.value?.user?.id) {
    console.error(
      'No new owner specified in the organization ownership request'
    );
    return;
  }
  console.debug('New owner', assocNewOwner.value.user.id, 'requested');
  console.debug(
    'Update of organization, new owner',
    assocNewOwner.value.user.id,
    'requested'
  );
  errorOrgOwnerUpdate.value = null;
  loadingOrgOwnerUpdate.value = true;
  updateOrgOwnerMut({ newOwnerId: assocNewOwner.value.user.id });
}
function handleRemoveAssociationRequest() {
  if (!assocToRemove.value?.id) {
    console.error('No association ID specified in the removal request');
    return;
  }
  console.debug(
    'Remove organization-user association',
    assocToRemove.value.id,
    'requested'
  );
  errorAssocRemove.value = null;
  loadingAssocRemove.value = true;
  removeOrgAssociationMut({ id: assocToRemove.value.id });
}

// --- Handlers ---
function updateAssociationButtonClicked(
  assoc: OrganizationUserAssociationType
) {
  assocToEdit.value = assoc;
  showAssocEditModal.value = true;
}

function setAsOwnerButtonClicked(assoc: OrganizationUserAssociationType) {
  if (!assoc) {
    console.error('No association to update');
    return;
  }
  assocNewOwner.value = assoc;
  showUpdateOwnerModal.value = true;
}

function removeAssociationButtonClicked(
  assoc: OrganizationUserAssociationType
) {
  assocToRemove.value = assoc;
  showRemoveModal.value = true;
}

const getRowClass = (item: any, type: string) => {
  // Only apply logic to data rows and only if the prop is enabled
  if (!item || type !== 'row' || !props.showActiveStatus) return;

  // Use your existing helper to check if this specific row is the active one
  if (isAssocActive(item)) {
    return 'fw-bold'; // Bootstrap 5 class for bold. Use 'font-weight-bold' for BS4.
  }
};

function rowClicked(
  assoc: OrganizationUserAssociationType,
  index: number,
  event: Event
) {
  emits('rowClicked', assoc);
}

function switchOrg(assoc: OrganizationUserAssociationType) {
  if (!assoc || !assoc.organization) {
    console.error('No organization to switch to');
    return;
  }
  assocToActivate.value = assoc;
  showAssocActivateModal.value = true;
}

function handleOrgActivated(orgId: number) {
  console.debug('Organization activated with ID:', orgId);
  emits('membershipsUpdated');
  userStore.activeOrgAssociation = assocToActivate.value;

  // clear the modal state with delay
  setTimeout(() => {
    assocToActivate.value = null;
    showAssocActivateModal.value = false;
  }, 1500);
}
function handleOrgActivateClose() {
  assocToActivate.value = null;
  showAssocActivateModal.value = false;
}

return (_ctx: any,_cache: any) => {
  const _component_font_awesome_icon = _resolveComponent("font-awesome-icon")!

  return (_openBlock(), _createElementBlock("div", null, [
    _createVNode(_unref(BCard), null, {
      default: _withCtx(() => [
        _createVNode(_unref(BTable), {
          items: filteredAssociations.value,
          busy: props.isLoading,
          fields: assocTableFields.value,
          small: "",
          "show-empty": "",
          striped: "",
          responsive: "",
          hover: "",
          class: "browser-table",
          "tbody-tr-class": getRowClass,
          onRowClicked: rowClicked
        }, {
          "table-busy": _withCtx(() => [
            _createElementVNode("div", _hoisted_1, [
              _createVNode(_unref(BSpinner), {
                small: "",
                class: "align-middle me-1"
              }),
              _cache[12] || (_cache[12] = _createElementVNode("span", { class: "ms-1" }, "Loading organization membership data...", -1))
            ])
          ]),
          empty: _withCtx(() => [...(_cache[13] || (_cache[13] = [
            _createElementVNode("div", { class: "text-center my-5" }, [
              _createElementVNode("p", { class: "text-muted" }, "No data found.")
            ], -1)
          ]))]),
          "cell(organizationName)": _withCtx((data) => [
            _createTextVNode(_toDisplayString(data.item.organization?.name) + " ", 1),
            (props.showActiveStatus && isAssocActive(data.item))
              ? (_openBlock(), _createBlock(_unref(BBadge), {
                  key: 0,
                  variant: "primary",
                  class: "ms-1",
                  title: "This is the currently active organization"
                }, {
                  default: _withCtx(() => [...(_cache[14] || (_cache[14] = [
                    _createTextVNode("A", -1)
                  ]))]),
                  _: 1
                }))
              : _createCommentVNode("", true)
          ]),
          "cell(userName)": _withCtx((data) => [
            _createElementVNode("span", null, _toDisplayString(data.item.user.givenName) + " " + _toDisplayString(data.item.user.familyName), 1)
          ]),
          "cell(id)": _withCtx((data) => [
            _createVNode(_unref(BButtonGroup), null, {
              default: _withCtx(() => [
                (
                !props.showActiveStatus &&
                !isAssocInactive(data.item) &&
                data.item.userRole !== _unref(ORGANIZATION_USER_ROLES).OWNER &&
                props.canManage
              )
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 0,
                      variant: "outline-warning",
                      size: "sm",
                      onClick: ($event: any) => (updateAssociationButtonClicked(data.item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: ['fas', 'pencil'] }),
                        _cache[15] || (_cache[15] = _createTextVNode(" Edit ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (
                !props.showActiveStatus &&
                props.isOwner &&
                !isAssocInactive(data.item) &&
                data.item.userRole !== _unref(ORGANIZATION_USER_ROLES).OWNER
              )
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 1,
                      variant: "outline-secondary",
                      size: "sm",
                      onClick: ($event: any) => (setAsOwnerButtonClicked(data.item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: ['fas', 'user'] }),
                        _cache[16] || (_cache[16] = _createTextVNode(" Set Owner ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
                  : _createCommentVNode("", true),
                (
                !isAssocInactive(data.item) &&
                (data.item.userRole !== _unref(ORGANIZATION_USER_ROLES).OWNER ||
                  isOwnerAndLastActiveMember.value)
              )
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 2,
                      variant: "outline-danger",
                      size: "sm",
                      onClick: ($event: any) => (removeAssociationButtonClicked(data.item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fas', 'person-walking-dashed-line-arrow-right'],
                          class: "me-1"
                        }),
                        (data.item.user.id === _unref(userStore).id)
                          ? (_openBlock(), _createElementBlock("span", _hoisted_2, "Leave"))
                          : (_openBlock(), _createElementBlock("span", _hoisted_3, "Remove"))
                      ]),
                      _: 2
                    }, 1032, ["onClick"]))
                  : _createCommentVNode("", true),
                (props.showActiveStatus && isAssocActive(data.item))
                  ? (_openBlock(), _createBlock(_unref(BButton), {
                      key: 3,
                      variant: "primary",
                      size: "sm",
                      onClick: ($event: any) => (switchOrg(data.item))
                    }, {
                      default: _withCtx(() => [
                        _createVNode(_component_font_awesome_icon, { icon: ['fas', 'check'] }),
                        _cache[17] || (_cache[17] = _createTextVNode(" Active ", -1))
                      ]),
                      _: 1
                    }, 8, ["onClick"]))
                  : (props.showActiveStatus && !isAssocInactive(data.item))
                    ? (_openBlock(), _createBlock(_unref(BButton), {
                        key: 4,
                        variant: "outline-primary",
                        size: "sm",
                        onClick: ($event: any) => (switchOrg(data.item))
                      }, {
                        default: _withCtx(() => [...(_cache[18] || (_cache[18] = [
                          _createTextVNode(" Activate ", -1)
                        ]))]),
                        _: 1
                      }, 8, ["onClick"]))
                    : _createCommentVNode("", true)
              ]),
              _: 2
            }, 1024)
          ]),
          _: 1
        }, 8, ["items", "busy", "fields"])
      ]),
      _: 1
    }),
    _createVNode(_unref(BFormCheckbox), {
      modelValue: showInactiveAssociations.value,
      "onUpdate:modelValue": _cache[0] || (_cache[0] = ($event: any) => ((showInactiveAssociations).value = $event)),
      name: "check-button",
      switch: ""
    }, {
      default: _withCtx(() => [...(_cache[19] || (_cache[19] = [
        _createTextVNode(" show inactive memberships ", -1)
      ]))]),
      _: 1
    }, 8, ["modelValue"]),
    (assocToActivate.value)
      ? (_openBlock(), _createBlock(OrganizationActivate, {
          key: 0,
          show: showAssocActivateModal.value,
          association: assocToActivate.value,
          onOrgActivated: handleOrgActivated,
          onClose: handleOrgActivateClose
        }, null, 8, ["show", "association"]))
      : _createCommentVNode("", true),
    (assocToEdit.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 1,
          id: "detailsModal",
          modelValue: showAssocEditModal.value,
          "onUpdate:modelValue": _cache[5] || (_cache[5] = ($event: any) => ((showAssocEditModal).value = $event))
        }, {
          title: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'pencil'],
              class: "me-1"
            }),
            _cache[20] || (_cache[20] = _createTextVNode(" Organization-user association details ", -1))
          ]),
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              variant: "secondary",
              onClick: _cache[4] || (_cache[4] = ($event: any) => (showAssocEditModal.value = false)),
              class: "me-2"
            }, {
              default: _withCtx(() => [...(_cache[21] || (_cache[21] = [
                _createTextVNode(" Cancel ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "primary",
              onClick: handleUpdateAssociationRequest,
              disabled: loadingAssocUpdate.value
            }, {
              default: _withCtx(() => [
                (loadingAssocUpdate.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_4, [
                      _createVNode(_unref(BSpinner), { small: "" }),
                      _cache[22] || (_cache[22] = _createTextVNode(" Saving Changes ", -1))
                    ]))
                  : (showAssocUpdateSuccess.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_5, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fa', 'check-double'],
                          class: "me-2"
                        }),
                        _cache[23] || (_cache[23] = _createTextVNode(" Changes Saved ", -1))
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_6, "Save Changes"))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          default: _withCtx(() => [
            _createElementVNode("p", null, _toDisplayString(assocToEdit.value.user?.email), 1),
            _createVNode(_unref(BFormSelect), {
              id: "assoc-edit-role",
              modelValue: assocToEdit.value.userRole,
              "onUpdate:modelValue": _cache[1] || (_cache[1] = ($event: any) => ((assocToEdit.value.userRole) = $event)),
              options: _unref(OrganizationMembershipModeSelectOptions),
              class: "form-control mb-2"
            }, null, 8, ["modelValue", "options"]),
            _createVNode(_unref(BFormTextarea), {
              modelValue: assocToEdit.value.description,
              "onUpdate:modelValue": _cache[2] || (_cache[2] = ($event: any) => ((assocToEdit.value.description) = $event)),
              id: "assoc-edit-description",
              label: "Description",
              placeholder: "Enter a description",
              type: "text",
              class: "mb-2"
            }, null, 8, ["modelValue"]),
            _createVNode(_unref(BAlert), {
              modelValue: errorAssocUpdate.value,
              "onUpdate:modelValue": _cache[3] || (_cache[3] = ($event: any) => ((errorAssocUpdate).value = $event)),
              variant: "danger",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(errorAssocUpdate.value), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (assocNewOwner.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 2,
          id: "confirmUpdateOwnerModal",
          modelValue: showUpdateOwnerModal.value,
          "onUpdate:modelValue": _cache[8] || (_cache[8] = ($event: any) => ((showUpdateOwnerModal).value = $event))
        }, {
          title: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'user-tie'],
              class: "me-1"
            }),
            _cache[24] || (_cache[24] = _createTextVNode(" Confirm ogranization ownership transfer ", -1))
          ]),
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              variant: "secondary",
              onClick: _cache[7] || (_cache[7] = ($event: any) => (showUpdateOwnerModal.value = false)),
              class: "me-2"
            }, {
              default: _withCtx(() => [...(_cache[27] || (_cache[27] = [
                _createTextVNode(" Cancel ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "danger",
              onClick: handleUpdateOrgOwnerRequest,
              disabled: loadingOrgOwnerUpdate.value
            }, {
              default: _withCtx(() => [
                (loadingOrgOwnerUpdate.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_7, [
                      _createVNode(_unref(BSpinner), { small: "" }),
                      _cache[28] || (_cache[28] = _createTextVNode(" Saving Changes ", -1))
                    ]))
                  : (showOrgOwnerUpdateSuccess.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_8, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fa', 'check-double'],
                          class: "me-2"
                        }),
                        _cache[29] || (_cache[29] = _createTextVNode(" Changes Saved ", -1))
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_9, "Confirm"))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          default: _withCtx(() => [
            _createElementVNode("p", null, [
              _cache[25] || (_cache[25] = _createTextVNode(" Are you sure you want to set ", -1)),
              _createElementVNode("strong", null, _toDisplayString(assocNewOwner.value.user?.email), 1),
              _cache[26] || (_cache[26] = _createTextVNode(" as new organization owner? As a result of this action, you will lose the organization ownership. You will become organization administrator. ", -1))
            ]),
            _createVNode(_unref(BAlert), {
              modelValue: errorOrgOwnerUpdate.value,
              "onUpdate:modelValue": _cache[6] || (_cache[6] = ($event: any) => ((errorOrgOwnerUpdate).value = $event)),
              variant: "danger",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(errorOrgOwnerUpdate.value), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true),
    (assocToRemove.value)
      ? (_openBlock(), _createBlock(_unref(BModal), {
          key: 3,
          id: "confirmRemoveModal",
          modelValue: showRemoveModal.value,
          "onUpdate:modelValue": _cache[11] || (_cache[11] = ($event: any) => ((showRemoveModal).value = $event))
        }, {
          title: _withCtx(() => [
            _createVNode(_component_font_awesome_icon, {
              icon: ['fas', 'person-walking-dashed-line-arrow-right'],
              class: "me-1"
            }),
            _cache[30] || (_cache[30] = _createTextVNode(" Confirm action ", -1))
          ]),
          footer: _withCtx(() => [
            _createVNode(_unref(BButton), {
              variant: "secondary",
              onClick: _cache[10] || (_cache[10] = ($event: any) => (showRemoveModal.value = false)),
              class: "me-2"
            }, {
              default: _withCtx(() => [...(_cache[32] || (_cache[32] = [
                _createTextVNode(" Cancel ", -1)
              ]))]),
              _: 1
            }),
            _createVNode(_unref(BButton), {
              variant: "danger",
              onClick: handleRemoveAssociationRequest,
              disabled: loadingAssocRemove.value
            }, {
              default: _withCtx(() => [
                (loadingAssocRemove.value)
                  ? (_openBlock(), _createElementBlock("span", _hoisted_13, [
                      _createVNode(_unref(BSpinner), { small: "" }),
                      _cache[33] || (_cache[33] = _createTextVNode(" Saving Changes ", -1))
                    ]))
                  : (showAssocRemoveSuccess.value)
                    ? (_openBlock(), _createElementBlock("span", _hoisted_14, [
                        _createVNode(_component_font_awesome_icon, {
                          icon: ['fa', 'check-double'],
                          class: "me-2"
                        }),
                        _cache[34] || (_cache[34] = _createTextVNode(" Changes Saved ", -1))
                      ]))
                    : (_openBlock(), _createElementBlock("span", _hoisted_15, "Confirm"))
              ]),
              _: 1
            }, 8, ["disabled"])
          ]),
          default: _withCtx(() => [
            (isOwnerAndLastActiveMember.value)
              ? (_openBlock(), _createElementBlock("div", _hoisted_10, [...(_cache[31] || (_cache[31] = [
                  _createElementVNode("p", null, " You are about to suspend the organization and give up the organization ownership. As a result of this action, this organization will become suspended indefinitely. No users will be able to join it. ", -1),
                  _createElementVNode("p", null, [
                    _createElementVNode("strong", null, " Considering adding a new user to the organization and transfer the ownership to them before proceeding. ")
                  ], -1),
                  _createElementVNode("p", null, "Are you sure you still want to proceed with this action?", -1)
                ]))]))
              : (assocToRemove.value.user?.id === _unref(userStore).id)
                ? (_openBlock(), _createElementBlock("p", _hoisted_11, " Are you sure you want to leave this organization? "))
                : (_openBlock(), _createElementBlock("p", _hoisted_12, " Are you sure you want to remove this user from the organization? ")),
            _createVNode(_unref(BAlert), {
              modelValue: errorAssocRemove.value,
              "onUpdate:modelValue": _cache[9] || (_cache[9] = ($event: any) => ((errorAssocRemove).value = $event)),
              variant: "danger",
              class: "mt-3"
            }, {
              default: _withCtx(() => [
                _createTextVNode(_toDisplayString(errorAssocRemove.value), 1)
              ]),
              _: 1
            }, 8, ["modelValue"])
          ]),
          _: 1
        }, 8, ["modelValue"]))
      : _createCommentVNode("", true)
  ]))
}
}

})